import React, { Suspense, lazy, useState, useEffect, useCallback } from "react";
import {
  Routes,
  Route,
  Link,
  Navigate,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import logo from "./site-logo.png";
import "bulma/css/bulma.min.css";
import "./bulma-pricingtable.min.css";
import "./app.scss";
import { faLock, faEnvelope, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AdminUser } from "./routes/admin";

const Home = lazy(() => import("./routes/home"));
const About = lazy(() => import("./routes/about"));
const Demos = lazy(() => import("./routes/demos"));
const Plans = lazy(() => import("./routes/plans"));
const FAQ = lazy(() => import("./routes/faq"));
const Privacy = lazy(() => import("./routes/privacy"));
const TermsOfService = lazy(() => import("./routes/terms_of_service"));
const GettingStarted = lazy(() => import("./routes/getting-started"));
const SignupForm = lazy(() => import("./routes/signup"));
const ResetPasswordForm = lazy(() => import("./routes/reset_password"));
const ForgotPasswordForm = lazy(() => import("./routes/forgot_password"));
const LoginForm = lazy(() => import("./routes/login"));
const AccountInformation = lazy(() => import("./routes/account"));
const PaymentForm = lazy(() => import("./routes/payment"));
const PaymentComplete = lazy(() => import("./routes/payment-complete"));
const Admin = lazy(() => import("./routes/admin"));
const Inventory = lazy(() => import("./routes/inventory"));
const Sales = lazy(() => import("./routes/sales"));
const Business = lazy(() => import("./routes/business"));
const Importer = lazy(() => import("./routes/importer"));
const NewItem = lazy(() => import("./routes/new_item"));
const Tags = lazy(() => import("./routes/tags"));
const NewBusiness = lazy(() => import("./routes/new_business"));

const PrivateRoute = ({ children, unauthenticated }) => {
  const location = useLocation();
  return !unauthenticated ? (
    <>{children}</>
  ) : (
    <Navigate
      replace={true}
      to="/login"
      state={{ from: `${location.pathname}${location.search}` }}
    />
  );
};

function App() {
  let navigate = useNavigate();
  let [searchParams, setSearchParams] = useSearchParams();
  const visitSource = searchParams.get("source");
  const redirectUrl = searchParams.get("redirect_url");

  const [account, setAccount] = useState({
    role: null,
    email: null,
    plan: null,
    business: {},
    poshmarkId: null,
    poshmarkHost: null,
    createdAt: null,
    stripe_subscription_status: null,
    trial_end_at: null,
    referralCode: null,
  });

  const [plan, setPlan] = useState("");
  const [price, setPrice] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  // this gets around the issue of having a redirect chain due to
  // the async nature of account fetching. for example, since account
  // state is nil, loggedIn = false
  // if we redirect based on this initial state, we will end up
  // doing ANOTHER redirect when the async fetch finishes.
  // so to prevent this, we reverse the state so that
  // we always attempt to load children but only redirect if the
  // server tells us otherwise
  const [unauthenticated, setUnauthenticated] = useState(false);

  const loggedIn = () => {
    return !!account.email;
  };

  const admin = () => {
    return account.role == "ADMIN";
  };

  const logout = () => {
    const fetchData = async () => {
      let response = await fetch("/api/sessions", {
        method: "DELETE",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      setAccount({
        email: null,
        plan: null,
        poshmarkId: null,
        poshmarkHost: null,
        business: {},
        poshmmarkAccounts: [],
        stripe_subscription_status: null,
        referralCode: null,
        trial_end_at: null,
        role: null,
      });
      setUnauthenticated(true);
      setClientSecret(null);
      navigate("/");
    };
    fetchData();
  };

  // authenticate & load
  useEffect(() => {
    const fetchData = async () => {
      let response = await fetch("/api/sessions", {
        method: "GET",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      try {
        let data = await response.json();
        if (data.status === "success") {
          setAccount({
            role: data.role,
            email: data.email,
            plan: data.plan_type,
            stripe_subscription_status: data.stripe_subscription_status,
            poshmarkId: data.poshmark_id,
            poshmarkHost: data.poshmark_host,
            poshmarkAccounts: data.poshmark_accounts,
            business: data.business,
            referralCode: data.referral_code,
            trial_end_at: data.trial_end_at,
            feature_flags: data.feature_flags,
          });
          setUnauthenticated(false);
        } else {
          setUnauthenticated(true);
        }
      } catch (error) {
        console.log(error.message);
      }
    };
    fetchData();
  }, []);

  const handleTagCreate = async (value) => {
    try {
      const response = await fetch("/api/inventory/tags", {
        method: "POST",
        body: JSON.stringify({ value: value }),
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        alert(
          "There was an error creating the tag! Please try again later or reach out to contact@closetwitch.com for support",
        );
        return;
      }

      setAccount({
        ...account,
        business: {
          ...account.business,
          tags: [...account.business.tags, value],
        },
      });

      return value;
    } catch (error) {
      alert(
        "There was an error creating the tag! Please try again later or reach out to contact@closetwitch.com for support",
      );
    }
  };

  const updateBusiness = async (businessData) => {
    fetch(`/api/business/${account.business.id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(businessData),
    });
    const res = {
      ...account,
      business: {
        ...account.business,
        ...businessData,
      },
    };
    setAccount(res);
    return res;
  };

  const dispatch = (action) => {
    if (action.type === "CREATE_NEW_TAG") {
      handleTagCreate(action.value);
    }
  };

  return (
    <>
      {!redirectUrl && (
        <Navbar
          admin={admin}
          loggedIn={loggedIn}
          logout={logout}
          account={account}
        />
      )}
      <Suspense fallback={<div className="loader"></div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about" element={<About />} />
          <Route path="/demos" element={<Demos />} />
          <Route
            path="plans"
            element={
              <Plans
                account={account}
                clientSecret={clientSecret}
                loggedIn={loggedIn}
                setPrice={setPrice}
                setPlan={setPlan}
                setClientSecret={setClientSecret}
              />
            }
          />
          <Route
            path="payment"
            element={
              <PaymentForm
                clientSecret={clientSecret}
                plan={plan}
                price={price}
              />
            }
          />
          <Route path="getting_started" element={<GettingStarted />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<TermsOfService />} />
          <Route
            path="account"
            element={
              <PrivateRoute unauthenticated={unauthenticated}>
                <AccountInformation account={account} setAccount={setAccount} />
              </PrivateRoute>
            }
          />
          <Route path="business" element={<Business account={account} />} />
          <Route path="inventory">
            <Route
              index={true}
              path="/inventory"
              element={
                <PrivateRoute unauthenticated={unauthenticated}>
                  <Inventory
                    account={account}
                    updateBusiness={updateBusiness}
                    dispatch={dispatch}
                  />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="import"
              element={<Importer account={account} setAccount={setAccount} />}
            />
            <Route
              path="new_item"
              element={
                <PrivateRoute unauthenticated={unauthenticated}>
                  <NewItem
                    account={account}
                    handleTagCreate={handleTagCreate}
                    updateBusiness={updateBusiness}
                  />
                </PrivateRoute>
              }
            />
            <Route
              path="sales"
              element={
                <PrivateRoute unauthenticated={unauthenticated}>
                  <Sales account={account} />
                </PrivateRoute>
              }
            />
          </Route>

          <Route path="tags" element={<Tags account={account} />} />
          <Route path="payment_complete" element={<PaymentComplete />} />
          <Route path="admin" element={<Admin admin={admin} />} />
          <Route
            path="admin/user/:userId"
            element={<AdminUser account={account} />}
          />
          <Route
            path="signup"
            element={
              <SignupForm
                setAccount={setAccount}
                setUnauthenticated={setUnauthenticated}
                loggedIn={loggedIn}
              />
            }
          />
          <Route
            path="forgot_password"
            element={
              <ForgotPasswordForm
                setAccount={setAccount}
                setUnauthenticated={setUnauthenticated}
                loggedIn={loggedIn}
              />
            }
          />
          <Route
            path="reset_password"
            element={
              <ResetPasswordForm
                setAccount={setAccount}
                setUnauthenticated={setUnauthenticated}
                loggedIn={loggedIn}
              />
            }
          />
          <Route
            path="referral/:referralCode"
            element={<SignupForm setAccount={setAccount} loggedIn={loggedIn} />}
          />
          <Route
            path="login"
            element={
              <LoginForm
                account={account}
                setUnauthenticated={setUnauthenticated}
                setAccount={setAccount}
                loggedIn={loggedIn}
              />
            }
          />
        </Routes>
      </Suspense>
      {!redirectUrl && (
        <section className="getintouch-section">
          <div className="container">
            <div className="columns mb-6 pt-4 px-6">
              <div className="column">
                <span className="logo-black mb-6"></span>
              </div>
            </div>
            <div className="columns mt-6 is-variable is-8 px-6 pb-6">
              <div className="column">
                <div className="mb-2">
                  <span className="is-uppercase has-text-weight-bold">
                    Get in touch
                  </span>
                </div>
                <div>
                  <ul>
                    <li>
                      <a
                        href="https://www.instagram.com/closetwitchco/"
                        target="_blank"
                      >
                        Instagram
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.facebook.com/witchesgetstuffdone"
                        target="_blank"
                      >
                        Facebook
                      </a>
                    </li>
                    <li>
                      <a>Newsletter</a>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="column">
                <div className="mb-2">
                  <span className="is-uppercase has-text-weight-bold">
                    Email
                  </span>
                </div>
                <p>contact@closetwitch.com</p>
              </div>
              <div className="column">
                <div className="mb-2">
                  <span className="is-uppercase has-text-weight-bold">
                    Location
                  </span>
                </div>
                <p>New Hartford, NY</p>
                <p>13413</p>
              </div>
              <div className="column">
                <div className="mb-2">
                  <span className="has-text-weight-bold">
                    <Link to="/privacy">Privacy Policy</Link>
                  </span>
                </div>
                <div className="mb-2">
                  <span className="has-text-weight-bold">
                    <Link to="/terms">Terms of Service</Link>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function Navbar({ admin, loggedIn, logout, account }) {
  const [menuToggle, setMenuToggle] = useState(false);

  return (
    <nav className="navbar" role="navigation" aria-label="main navigation">
      <div className="navbar-brand">
        <a className="navbar-item logo" href="/"></a>
        <a
          role="button"
          className={`navbar-burger ${menuToggle ? "is-active" : ""}`}
          aria-label="menu"
          aria-expanded="false"
          data-target="navbarBasicExample"
          onClick={() => setMenuToggle(!menuToggle)}
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </a>
      </div>

      <div
        id="navbarBasicExample"
        className={`navbar-menu ${menuToggle ? "is-active" : ""}`}
      >
        <div className="navbar-start">
          <div className="navbar-item">
            <div className="buttons">
              {!loggedIn() && (
                <Link
                  className="button is-primary"
                  to="/signup"
                  onClick={() => setMenuToggle(false)}
                >
                  <strong>Sign up</strong>
                </Link>
              )}
              {!loggedIn() && (
                <Link
                  className="button is-light"
                  to="/login"
                  onClick={() => setMenuToggle(false)}
                >
                  Log in
                </Link>
              )}
            </div>
          </div>

          <Link
            className="navbar-item"
            to="/"
            onClick={(event) => {
              setMenuToggle(false);
              event.target.blur();
            }}
          >
            Home
          </Link>
          <div className={`navbar-item has-dropdown is-hoverable`}>
            <Link
              className="navbar-link"
              to="/plans"
              onClick={(event) => {
                setMenuToggle(false);
                event.target.blur();
              }}
            >
              Plans
            </Link>
            <div className="navbar-dropdown">
              <Link
                className="navbar-item"
                to="/plans"
                onClick={(event) => {
                  setMenuToggle(false);
                  event.target.blur();
                }}
              >
                Plans
              </Link>
              <Link
                className="navbar-item"
                to="/getting_started"
                onClick={(event) => {
                  setMenuToggle(false);
                  event.target.blur();
                }}
              >
                Getting Started
              </Link>
              <Link
                className="navbar-item"
                to="/demos"
                onClick={(event) => {
                  setMenuToggle(false);
                  event.target.blur();
                }}
              >
                Demos
              </Link>
            </div>
          </div>
          <Link
            className="navbar-item"
            to="/about"
            onClick={() => setMenuToggle(false)}
          >
            About
          </Link>
          <Link
            className="navbar-item"
            to="/faq"
            onClick={() => setMenuToggle(false)}
          >
            FAQ
          </Link>
          <a
            href="https://closetwitch.com/blog"
            className="navbar-item"
            target="_blank"
          >
            Blog
          </a>
          <div className="navbar-item has-dropdown is-hoverable">
            <a className="navbar-link">Contact</a>
            <div className="navbar-dropdown">
              <a className="navbar-item">
                Report an issue at contact@closetwitch.com
              </a>
            </div>
          </div>
        </div>

        <div className="navbar-end">
          <div className="navbar-item">
            <div className="buttons">
              {admin() && (
                <Link className="button is-link" to="/admin">
                  Admin
                </Link>
              )}
              {loggedIn() && (
                <Link className="button is-primary" to="/account">
                  Account
                </Link>
              )}

              {loggedIn() &&
                (account.plan == "PRO" || account.plan == "PRO_LITE") && (
                  <div className="mr-2">
                    <Link
                      className="button is-link"
                      to={{
                        pathname: "/inventory",
                      }}
                    >
                      Inventory
                    </Link>
                  </div>
                )}
              {loggedIn() &&
                (account.plan == "PRO" || account.plan == "PRO_LITE") && (
                  <div className="mr-2">
                    <Link
                      className="button is-info"
                      to={{
                        pathname: "/inventory/sales",
                      }}
                    >
                      Sales
                    </Link>
                  </div>
                )}
              {loggedIn() && (
                <a className="button is-light ml-3" onClick={logout}>
                  Logout
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export function PasswordInputField({
  onKeyDown,
  onChange,
  onBlur,
  password,
  placeholder,
  error,
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="field">
      <p className="control has-icons-left has-icons-right">
        <input
          className="input"
          type={visible ? "text" : "password"}
          placeholder={placeholder || "Password"}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onChange={onChange}
          value={password}
        />
        <span
          className="icon is-small is-left"
          onClick={() => {
            setVisible(false);
          }}
        >
          <FontAwesomeIcon icon={faLock} />
        </span>
        <span
          className="icon is-small is-right"
          onClick={() => {
            setVisible(!visible);
          }}
        >
          <FontAwesomeIcon icon={faEye} />
        </span>
      </p>
      {error && <p class="help is-danger">{error}</p>}
    </div>
  );
}

export function EmailInputField({
  onKeyDown,
  onChange,
  disabled,
  onBlur,
  value,
  error,
}) {
  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
  };
  return (
    <div className="field">
      <div className="control has-icons-left has-icons-right">
        <input
          className="input"
          type="email"
          placeholder="Email"
          disabled={disabled}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
        />
        <span className="icon is-small is-left">
          <FontAwesomeIcon icon={faEnvelope} />
        </span>
        <span className="icon is-small is-right">
          <i className="fas fa-exclamation-triangle"></i>
        </span>
      </div>
      {error && <p className="help is-danger">{error}</p>}
    </div>
  );
}

export default App;
